import {
  AVAILABILITY_DAYS,
  AVAILABILITY_TIME_SLOTS,
  WORK_FREQUENCIES,
  WORK_TYPES,
  type WorkFrequency,
} from '@teentack/shared';
import { SelectInput } from '@/components/ui/Input';
import { ChoiceChip } from '@/components/signup/ChoiceChip';
import {
  DAY_LABELS,
  TIME_SLOT_LABELS,
  WORK_FREQUENCY_LABELS,
  WORK_TYPE_LABELS,
  type AvailabilityValue,
} from './availability-vocabulary';

function toggle<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list.filter((entry) => entry !== item) : [...list, item];
}

/**
 * Editing for the two JSON columns on `teens`, shared by the signup wizard and
 * the profile page so both write the same shape and read the same labels.
 */
export function AvailabilityFields({
  value,
  onChange,
}: {
  value: AvailabilityValue;
  onChange: (next: AvailabilityValue) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <p className="mb-2.5 text-sm font-semibold text-ink">Days you are free</p>
        <div className="flex flex-wrap gap-2">
          {AVAILABILITY_DAYS.map((day) => (
            <ChoiceChip
              key={day}
              selected={value.days.includes(day)}
              onToggle={() => onChange({ ...value, days: toggle(value.days, day) })}
            >
              {DAY_LABELS[day]}
            </ChoiceChip>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2.5 text-sm font-semibold text-ink">Times that suit you</p>
        <div className="flex flex-wrap gap-2">
          {AVAILABILITY_TIME_SLOTS.map((slot) => (
            <ChoiceChip
              key={slot}
              selected={value.timeSlots.includes(slot)}
              onToggle={() => onChange({ ...value, timeSlots: toggle(value.timeSlots, slot) })}
            >
              {TIME_SLOT_LABELS[slot]}
            </ChoiceChip>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2.5 text-sm font-semibold text-ink">Kind of work you want</p>
        <div className="flex flex-wrap gap-2">
          {WORK_TYPES.map((type) => (
            <ChoiceChip
              key={type}
              selected={value.workTypes.includes(type)}
              onToggle={() => onChange({ ...value, workTypes: toggle(value.workTypes, type) })}
            >
              {WORK_TYPE_LABELS[type]}
            </ChoiceChip>
          ))}
        </div>
      </div>

      <SelectInput
        label="How often you want to work"
        value={value.frequency}
        onChange={(event) =>
          onChange({ ...value, frequency: event.target.value as WorkFrequency | '' })
        }
        optional
      >
        <option value="">No preference</option>
        {WORK_FREQUENCIES.map((entry) => (
          <option key={entry} value={entry}>
            {WORK_FREQUENCY_LABELS[entry]}
          </option>
        ))}
      </SelectInput>
    </div>
  );
}
